import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Crown, ArrowLeft, Send, MessagesSquare, ChevronUp, Award } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");

export default function ForumThread() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined); // undefined loading, null error
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/forum/threads/${id}`).then(({ data }) => setData(data)).catch(() => setData(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const upvote = async (postId) => {
    if (!user) { navigate("/login"); return; }
    try { await api.post(`/forum/posts/${postId}/upvote`); load(); }
    catch { toast.error("Could not vote"); }
  };

  const markBest = async (postId) => {
    try { await api.post(`/forum/threads/${id}/best/${postId}`); load(); }
    catch { toast.error("Could not mark best answer"); }
  };

  const reply = async () => {
    if (!user) { navigate("/login"); return; }
    if (!body.trim()) return toast.error("Write a reply first.");
    setSending(true);
    try {
      await api.post(`/forum/threads/${id}/posts`, { body });
      setBody("");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not reply");
      if (e.response?.status === 402) navigate("/billing");
    } finally {
      setSending(false);
    }
  };

  if (data === undefined)
    return <div className="min-h-screen bp-grid"><Navbar /><div className="flex justify-center py-32"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div></div>;

  if (data === null)
    return (
      <div className="min-h-screen bp-grid"><Navbar />
        <div className="mx-auto flex max-w-md flex-col items-center px-4 py-28 text-center">
          <MessagesSquare className="h-12 w-12 text-muted-foreground" />
          <h1 className="mt-4 font-heading text-2xl font-bold">Thread unavailable</h1>
          <p className="mt-2 text-muted-foreground">It may have been removed or is VIP-only.</p>
          <Link to="/forum" className="mt-6"><Button className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black">Back to forum</Button></Link>
        </div>
      </div>
    );

  const { thread, posts } = data;
  const Author = ({ name, vip, at }) => (
    <div className="flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">
      <span className="text-foreground">{name}</span>
      {vip && <span className="inline-flex items-center gap-1 text-primary"><Crown className="h-3 w-3" /> VIP</span>}
      · {fmt(at)}
    </div>
  );

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-8">
        <Link to="/forum" className="inline-flex items-center gap-2 font-mono2 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary" data-testid="back-to-forum">
          <ArrowLeft className="h-4 w-4" /> Forum
        </Link>

        <div className="mt-4 border border-border p-6" data-testid="thread-op">
          <span className="border border-border px-2 py-0.5 font-mono2 text-[10px] uppercase tracking-widest text-primary">{thread.category_label}</span>
          <h1 className="mt-3 font-heading text-2xl font-extrabold tracking-tight">{thread.title}</h1>
          <div className="mt-2"><Author name={thread.author_name} vip={thread.author_vip} at={thread.created_at} /></div>
          <p className="mt-4 whitespace-pre-wrap leading-relaxed text-foreground">{thread.body}</p>
        </div>

        <p className="mt-8 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">{posts.length} {posts.length === 1 ? "reply" : "replies"}</p>
        <div className="mt-3 space-y-3" data-testid="reply-list">
          {posts.map((p) => (
            <div key={p.post_id} className={`flex gap-4 border p-5 ${p.is_best ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`reply-${p.post_id}`}>
              <div className="flex flex-col items-center gap-1 pt-1">
                <button onClick={() => upvote(p.post_id)} data-testid={`upvote-${p.post_id}`}
                  className={`flex h-9 w-9 items-center justify-center border transition-colors ${p.upvoted ? "border-primary bg-primary text-black" : "border-border text-muted-foreground hover:border-primary hover:text-primary"}`}>
                  <ChevronUp className="h-4 w-4" />
                </button>
                <span className="font-mono2 text-xs font-bold" data-testid={`upvote-count-${p.post_id}`}>{p.upvotes}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <Author name={p.author_name} vip={p.author_vip} at={p.created_at} />
                  {p.is_best && <span className="inline-flex items-center gap-1 border border-primary px-2 py-0.5 font-mono2 text-[10px] uppercase tracking-widest text-primary"><Award className="h-3 w-3" /> Best answer</span>}
                </div>
                <p className="mt-2 whitespace-pre-wrap leading-relaxed text-foreground">{p.body}</p>
                {thread.can_mark_best && (
                  <button onClick={() => markBest(p.post_id)} data-testid={`mark-best-${p.post_id}`}
                    className="mt-3 inline-flex items-center gap-1 font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary">
                    <Award className="h-3 w-3" /> {p.is_best ? "Unmark best" : "Mark best answer"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 border border-border border-l-2 border-l-primary p-5">
          {user ? (
            <>
              <Textarea data-testid="reply-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4}
                placeholder="Add your reply…" className="rounded-sm border-border bg-transparent" />
              <div className="mt-3 flex justify-end">
                <Button onClick={reply} disabled={sending} data-testid="reply-submit"
                  className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                  {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Reply
                </Button>
              </div>
            </>
          ) : (
            <p className="text-center font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline">Sign in</Link> to join the discussion
            </p>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
